import type { QueryObserverResult } from '@tanstack/react-query'

/**
 * Devuelve el resultado de una query gateada por permisos con un `refetch` que respeta ese gate.
 *
 * `enabled: false` solo frena la ejecución automática: un `refetch()` manual corre la queryFn igual. En
 * una query cuyo select depende de un permiso, eso pediría los datos con los permisos base, antes de
 * los overrides del rol, o sin el permiso que la query exige. `skipToken` no sirve: con él, `refetch()`
 * deja la query en error.
 *
 * `permitida` es la parte del `enabled` que sale de los permisos: `permisosListos` y, si la query lo
 * exige, el permiso propio. Mientras no es true, `refetch()` devuelve el resultado actual sin llamar a
 * la queryFn. El resto del resultado se lee del original a través de un Proxy, así TanStack Query sigue
 * viendo qué propiedades usa el componente y no re-renderiza de más.
 */
export const conRefetchSoloPermitido = <TResultado extends Pick<QueryObserverResult, 'refetch'>>(
  resultado: TResultado,
  permitida: boolean,
): TResultado => {
  if (permitida) return resultado
  const refetchEnEspera = () => Promise.resolve(resultado)
  return new Proxy(resultado, {
    get: (target, propiedad, receiver) =>
      propiedad === 'refetch' ? refetchEnEspera : Reflect.get(target, propiedad, receiver),
  })
}
